/**
 * ChatList Component
 * Lista virtualizzata delle chat con react-window v2 + infinite scroll
 */

import { memo, useCallback, useState, useEffect, useMemo } from 'react';
import { MessageSquare, Loader2, Clock, AlertCircle, RefreshCw } from 'lucide-react';
import { List, useListRef, useDynamicRowHeight } from 'react-window';
import { ChatListItem } from './ChatListItem';
import { useChatRowPrefs } from './useChatRowPrefs';
import type { MuteOption } from '@/lib/services/whatsapp/chatListRules';
import { useWhatsAppAgentStore } from '@/lib/stores/whatsappAgentStore';
import { useChatRecontactStore } from '@/lib/stores/chatRecontactStore';
import { recontactStatus, type ChatRecontact } from '@/lib/services/whatsapp/recontactRules';
import type { ChatOverview, ContactPresence } from '@/lib/types/whatsappChat';
import type { ChatLabel } from '@/lib/stores/whatsappChatMetaStore';
import type { ChatAssignment } from '@/lib/types/whatsappAgent';

// Altezza stimata di una riga semplice: quella vera la misura la lista (righe con
// etichette e cliente abbinato sono piu' alte e prima si sovrapponevano).
const ESTIMATED_ITEM_HEIGHT = 66;
const OVERSCAN_COUNT = 5;
const LOAD_MORE_THRESHOLD = 5;

interface ChatListProps {
  chats: ChatOverview[];
  selectedChatId: string | null;
  presenceMap: Map<string, ContactPresence>;
  chatLabels?: Map<string, ChatLabel[]>;
  drafts?: Record<string, string>;
  onSelectChat: (chatId: string) => void;
  onMarkAsRead?: (chatId: string) => void;
  onMarkAsUnread?: (chatId: string) => void;
  loading?: boolean;
  error?: string | null;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  onRetry?: () => void;
  /** Org per fissa/archivia/silenzia. */
  organizationId?: string | null;
  /** Testo dello stato vuoto (es. filtri o ricerca attivi). */
  emptyMessage?: string;
  /** Contenuto al posto dello stato vuoto (es. "Scrivi a +39 ..."). */
  emptyContent?: React.ReactNode;
}

type RowPrefs = ReturnType<typeof useChatRowPrefs>;

// Props extra passate ad ogni riga via rowProps
interface ChatRowData {
  validChats: ChatOverview[];
  selectedChatId: string | null;
  presenceMap: Map<string, ContactPresence>;
  chatLabels?: Map<string, ChatLabel[]>;
  drafts?: Record<string, string>;
  pendingSuggestionCounts: Map<string, number>;
  assignmentsMap: Map<string, ChatAssignment>;
  onSelectChat: (chatId: string) => void;
  onMarkAsRead?: (chatId: string) => void;
  onMarkAsUnread?: (chatId: string) => void;
  loadingMore?: boolean;
  prefs: RowPrefs;
  onTogglePin: (chatId: string) => void;
  onToggleArchive: (chatId: string) => void;
  onMute: (chatId: string, option: MuteOption | null) => void;
  reminders: Map<string, ChatRecontact>;
  organizationId?: string | null;
}

// Row component per react-window v2
const ChatRow = memo(function ChatRow({
  index,
  style,
  validChats,
  selectedChatId,
  presenceMap,
  chatLabels,
  drafts,
  pendingSuggestionCounts,
  assignmentsMap,
  onSelectChat,
  onMarkAsRead,
  onMarkAsUnread,
  loadingMore,
  prefs,
  onTogglePin,
  onToggleArchive,
  onMute,
  reminders,
  organizationId,
}: {
  ariaAttributes: Record<string, unknown>;
  index: number;
  style: React.CSSProperties;
} & ChatRowData) {
  if (index >= validChats.length) {
    if (loadingMore) {
      return (
        <div style={style} className="flex justify-center items-center">
          <Loader2 className="w-4 h-4 text-[#2383E2] animate-spin" />
        </div>
      );
    }
    return null;
  }

  const chat = validChats[index];
  const presence = presenceMap?.get(chat.id);
  // Usa direttamente chat.unreadCount da WAHA (fonte unica di verità, aggiornato ogni 15s dal polling)
  const unreadCount = chat.unreadCount || 0;
  const labels = chatLabels?.get(chat.id) || [];
  const state = prefs.stateOf(chat.id, chat);
  const reminder = reminders.get(chat.id);
  const recontact = reminder ? { ...recontactStatus(reminder, chat), dueAt: reminder.dueAt, note: reminder.note } : null;

  return (
    <div style={style}>
      <ChatListItem
        chat={chat}
        isSelected={selectedChatId === chat.id}
        isOnline={presence?.status === 'online'}
        isTyping={presence?.status === 'typing' || presence?.status === 'recording'}
        unreadCount={unreadCount}
        labels={labels}
        draft={drafts?.[chat.id]}
        suggestionCount={pendingSuggestionCounts.get(chat.id) || 0}
        assignment={assignmentsMap.get(chat.id) || null}
        onClick={() => onSelectChat(chat.id)}
        onMarkAsRead={onMarkAsRead}
        onMarkAsUnread={onMarkAsUnread}
        pinned={state.pinned}
        archived={state.archived}
        muted={state.muted}
        prefsAvailable={prefs.prefsAvailable}
        onTogglePin={onTogglePin}
        onToggleArchive={onToggleArchive}
        onMute={onMute}
        recontact={recontact}
        organizationId={organizationId}
      />
    </div>
  );
});

function LoadingSkeleton() {
  const [showExtendedMessage, setShowExtendedMessage] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowExtendedMessage(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="space-y-2 p-2">
      {showExtendedMessage && (
        <div className="mx-2 mb-4 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-[#2383E2] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[#2383E2] mb-1">
                Caricamento in corso...
              </p>
              <p className="text-xs text-[#787774]">
                Il primo caricamento potrebbe richiedere qualche minuto, specialmente se hai molte chat. Puoi continuare a usare il CRM e tornare più tardi.
              </p>
            </div>
          </div>
        </div>
      )}
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex gap-3 p-3 animate-pulse">
          <div className="w-10 h-10 bg-[rgba(55,53,47,0.08)] dark:bg-white/10 rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-[rgba(55,53,47,0.08)] dark:bg-white/10 rounded w-3/4" />
            <div className="h-3 bg-[rgba(55,53,47,0.08)] dark:bg-white/10 rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ message = 'Nessuna chat trovata' }: { message?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="w-12 h-12 rounded-full bg-[rgba(55,53,47,0.08)] dark:bg-white/10 flex items-center justify-center mb-3">
        <MessageSquare className="w-6 h-6 text-[#787774]" />
      </div>
      <p className="text-sm text-[#787774]">{message}</p>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="m-3 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-red-700 dark:text-red-300 mb-1">Non sono riuscito a caricare le chat</p>
          <p className="text-xs text-[#787774] break-words">{message}</p>
          {onRetry && (
            <button type="button" onClick={onRetry} className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-white/10 border border-[rgba(55,53,47,0.16)] dark:border-white/10 text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.04)]">
              <RefreshCw className="w-3.5 h-3.5" />Riprova
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export const ChatList = memo(function ChatList({
  chats = [],
  selectedChatId,
  presenceMap,
  chatLabels,
  drafts,
  onSelectChat,
  onMarkAsRead,
  onMarkAsUnread,
  loading = false,
  error,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  onRetry,
  organizationId,
  emptyMessage,
  emptyContent,
}: ChatListProps) {
  const listRef = useListRef();
  const rowHeight = useDynamicRowHeight({ defaultRowHeight: ESTIMATED_ITEM_HEIGHT });
  const prefs = useChatRowPrefs(organizationId);
  const { togglePin, toggleArchive, mute } = prefs;

  // Badge suggerimenti AI per-chat (hook DEVE stare prima di qualsiasi return)
  const pendingSuggestionCounts = useWhatsAppAgentStore(s => s.pendingSuggestionCounts);
  const assignments = useWhatsAppAgentStore(s => s.assignments);
  const reminders = useChatRecontactStore(s => s.reminders);

  // Mappa chat_jid → assignment per lookup O(1)
  const assignmentsMap = useMemo(() => {
    const map = new Map<string, ChatAssignment>();
    for (const a of assignments) {
      map.set(a.chat_jid, a);
    }
    return map;
  }, [assignments]);

  // Dedup + validazione con Set (O(n) invece di O(n²) con findIndex)
  const validChats = useMemo(() => {
    const seen = new Set<string>();
    return chats.filter((chat) => {
      if (!chat.id || chat.id === 'unknown') return false;
      if (seen.has(chat.id)) return false;
      seen.add(chat.id);
      return true;
    });
  }, [chats]);

  // Infinite scroll via onRowsRendered (v2 API)
  const handleRowsRendered = useCallback(
    (visibleRange: { startIndex: number; stopIndex: number }) => {
      if (!hasMore || loadingMore || !onLoadMore) return;
      if (visibleRange.stopIndex >= validChats.length - LOAD_MORE_THRESHOLD) {
        onLoadMore();
      }
    },
    [hasMore, loadingMore, onLoadMore, validChats.length]
  );

  const chatById = useMemo(() => new Map(validChats.map((c) => [c.id, c])), [validChats]);
  const onTogglePin = useCallback((id: string) => { const c = chatById.get(id); if (c) togglePin(id, c); }, [chatById, togglePin]);
  const onToggleArchive = useCallback((id: string) => { const c = chatById.get(id); if (c) toggleArchive(id, c); }, [chatById, toggleArchive]);
  const onMute = useCallback((id: string, opt: MuteOption | null) => mute(id, opt), [mute]);

  // 'sync_in_progress' non e' un errore: WAHA sta ancora sincronizzando le chat del telefono.
  if (error === 'sync_in_progress') return <LoadingSkeleton />;
  if (error && validChats.length === 0) return <ErrorState message={error} onRetry={onRetry} />;

  if (loading) return <LoadingSkeleton />;
  if (!chats || chats.length === 0 || validChats.length === 0) {
    return <>{emptyContent ?? <EmptyState message={emptyMessage} />}</>;
  }

  const rowCount = validChats.length + (loadingMore ? 1 : 0);

  // Props passate a ogni riga
  const rowProps = {
    validChats,
    selectedChatId,
    presenceMap,
    chatLabels,
    drafts,
    pendingSuggestionCounts,
    assignmentsMap,
    onSelectChat,
    onMarkAsRead,
    onMarkAsUnread,
    loadingMore,
    prefs,
    onTogglePin,
    onToggleArchive,
    onMute,
    reminders,
    organizationId,
  };

  return (
    <div className="flex-1 overflow-hidden p-2">
      <List
        listRef={listRef}
        rowCount={rowCount}
        rowHeight={rowHeight}
        rowComponent={ChatRow}
        rowProps={rowProps}
        overscanCount={OVERSCAN_COUNT}
        onRowsRendered={handleRowsRendered}
        style={{ height: '100%' }}
      />
    </div>
  );
});
